import api from './client';

// =============================================================================
// Protocol API (Spec 034)
// =============================================================================

export interface ProtocolCategory {
  id: number;
  organizationId: number;
  name: string;
  description?: string;
  icon?: string;
  color?: string;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
}

export interface Protocol {
  id: number;
  publicId: string;
  organizationId: number;
  categoryId?: number;
  categoryName?: string;
  categoryIcon?: string;
  categoryColor?: string;
  title: string;
  description?: string; // HTML content from rich editor
  visibility: 'org_wide' | 'assigned_only' | 'private';
  importance: 'essential' | 'standard' | 'preferred';
  isActive: boolean;
  effectiveDate?: string;
  trackingType?: 'task_based' | 'continuous' | 'untracked';
  createdBy: number;
  creatorName?: string;
  createdAt: string;
  updatedAt: string;
  assignmentCount?: number;
  isAssignedToUser?: boolean;
}

export interface ProtocolTemplate {
  id: number;
  title: string;
  description?: string;
  categoryId?: number;
  importance: string;
  content: string;
  isSystem: boolean;
  organizationId?: number;
  // UUID public id of the owning org. Numeric `organizationId` is a raw DB id
  // and must NOT be compared against `Organization.id` (a public-id string) —
  // see bug-registry "id-shape mismatch" (2026-05-18). Ownership checks use
  // this string-vs-string.
  organizationPublicId?: string;
  createdBy?: number;
  parentTemplateId?: number;
  forkCount: number;
  tags: string[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ProtocolAssignment {
  id: number;
  protocolId: number;
  userId: number;
  userName?: string;
  userEmail?: string;
  assignedBy: number;
  assignedByName?: string;
  status: 'active' | 'paused' | 'removed';
  notes?: string;
  assignedAt: string;
  updatedAt: string;
}

// =============================================================================
// Compliance Types (Spec 101 + 110)
// =============================================================================

export interface ProtocolComplianceData {
  protocolId: number;
  protocolPublicId: string;
  protocolTitle: string;
  importance: string;
  trackingType: string;
  assignmentId: number;
  userId: number;
  userName: string;
  complianceRate: number | null;
  completedCount: number;
  missedCount: number;
  lapseCount: number;
  excusedCount: number;
  totalExpected: number;
  currentStreak: number;
  trend: 'up' | 'down' | 'stable';
}

export interface ComplianceSummary {
  overallRate: number;
  totalProtocols: number;
  greenCount: number;
  yellowCount: number;
  redCount: number;
  topPerformer: { userName: string; rate: number } | null;
  needsAttention: { protocolTitle: string; userName: string; rate: number }[];
}

export interface HeatmapEntry {
  date: string;
  status: 'completed' | 'missed' | 'lapse' | 'excused' | 'none';
}

export interface LapseReport {
  id: number;
  publicId: string;
  protocolId: number;
  assignmentId: number;
  reportedBy: number;
  reportedByName?: string;
  organizationId: number;
  lapseDate: string;
  description?: string;
  protocolTitle?: string;
  acknowledged: boolean;
  acknowledgedAt?: string;
  acknowledgedBy?: number;
  acknowledgedByName?: string;
  createdAt: string;
}

export const protocolAPI = {
  // =============================================================================
  // Categories
  // =============================================================================

  /** Get all protocol categories for the organization */
  getCategories: () =>
    api.get<{ data: ProtocolCategory[] }>('/protocols/categories'),

  /** Create a new protocol category (admin only) */
  createCategory: (data: {
    name: string;
    description?: string;
    icon?: string;
    color?: string;
  }) => api.post<{ data: ProtocolCategory }>('/protocols/categories', data),

  /** Update a protocol category (admin only) */
  updateCategory: (
    categoryId: number | string,
    data: {
      name?: string;
      description?: string;
      icon?: string;
      color?: string;
      sort_order?: number;
    }
  ) =>
    api.put<{ data: ProtocolCategory }>(
      `/protocols/categories/${categoryId}`,
      data
    ),

  /** Delete a protocol category (admin only) */
  deleteCategory: (categoryId: number | string) =>
    api.delete<{ success: boolean }>(`/protocols/categories/${categoryId}`),

  // =============================================================================
  // Protocols
  // =============================================================================

  /** Get all protocols with optional filtering */
  getProtocols: (options?: {
    category_id?: number;
    visibility?: 'org_wide' | 'assigned_only' | 'private';
    importance?: 'essential' | 'standard' | 'preferred';
    activeOnly?: boolean;
    page?: number;
    limit?: number;
  }) => {
    const params = new URLSearchParams();
    if (options?.category_id)
      params.append('category_id', options.category_id.toString());
    if (options?.visibility) params.append('visibility', options.visibility);
    if (options?.importance) params.append('importance', options.importance);
    if (options?.activeOnly !== undefined)
      params.append('activeOnly', options.activeOnly.toString());
    if (options?.page) params.append('page', options.page.toString());
    if (options?.limit) params.append('limit', options.limit.toString());
    const queryString = params.toString();
    return api.get<{ data: Protocol[]; pagination?: { total: number; page: number; limit: number } }>(
      `/protocols${queryString ? `?${queryString}` : ''}`
    );
  },

  /** Get a single protocol by ID */
  getProtocol: (protocolId: number | string) =>
    api.get<{ data: Protocol }>(`/protocols/${protocolId}`),

  /** Create a new protocol (admin only) */
  createProtocol: (data: {
    categoryId?: number;
    title: string;
    description?: string;
    visibility?: 'org_wide' | 'assigned_only' | 'private';
    importance?: 'essential' | 'standard' | 'preferred';
    effective_date?: string;
    is_active?: boolean;
    tracking_type?: 'task_based' | 'continuous' | 'untracked';
  }) => api.post<{ data: Protocol }>('/protocols', data),

  /** Update a protocol (admin only) */
  updateProtocol: (
    protocolId: number | string,
    data: {
      categoryId?: number | null;
      title?: string;
      description?: string;
      visibility?: 'org_wide' | 'assigned_only' | 'private';
      importance?: 'essential' | 'standard' | 'preferred';
      is_active?: boolean;
      effective_date?: string;
      tracking_type?: 'task_based' | 'continuous' | 'untracked';
    }
  ) => api.put<{ data: Protocol }>(`/protocols/${protocolId}`, data),

  /** Delete a protocol (admin only) */
  deleteProtocol: (protocolId: number | string) =>
    api.delete<{ success: boolean }>(`/protocols/${protocolId}`),

  // =============================================================================
  // Assignments
  // =============================================================================

  /** Get assignments for a protocol (admin only) */
  getAssignments: (protocolId: number | string) =>
    api.get<{ data: ProtocolAssignment[] }>(
      `/protocols/${protocolId}/assignments`
    ),

  /** Assign protocol to users (admin only) */
  assignProtocol: (
    protocolId: number | string,
    data: {
      user_ids: (number | string)[];
      notes?: string;
    }
  ) =>
    api.post<{ data: ProtocolAssignment[] }>(
      `/protocols/${protocolId}/assignments`,
      data
    ),

  /** Update an assignment (admin only) */
  updateAssignment: (
    protocolId: number | string,
    userId: number | string,
    data: {
      status?: 'active' | 'paused' | 'removed';
      notes?: string;
    }
  ) =>
    api.put<{ data: ProtocolAssignment }>(
      `/protocols/${protocolId}/assignments/${userId}`,
      data
    ),

  /** Remove an assignment (admin only) */
  removeAssignment: (protocolId: number | string, userId: number | string) =>
    api.delete<{ success: boolean }>(
      `/protocols/${protocolId}/assignments/${userId}`
    ),

  // =============================================================================
  // User-facing
  // =============================================================================

  /** Get protocols assigned to the current user */
  getMyProtocols: (options?: { activeOnly?: boolean; page?: number; limit?: number }) => {
    const params = new URLSearchParams();
    if (options?.activeOnly !== undefined)
      params.append('activeOnly', options.activeOnly.toString());
    if (options?.page) params.append('page', options.page.toString());
    if (options?.limit) params.append('limit', options.limit.toString());
    const queryString = params.toString();
    return api.get<{
      data: Array<Protocol & { status: string; notes?: string; assignedAt: string }>;
      pagination: { total: number; page: number; limit: number };
    }>(`/protocols/my${queryString ? `?${queryString}` : ''}`);
  },

  // =============================================================================
  // Templates
  // =============================================================================

  /** Get all available protocol templates */
  getTemplates: () =>
    api.get<{ data: ProtocolTemplate[] }>('/protocols/templates'),

  /** Get a single template by ID */
  getTemplate: (templateId: number) =>
    api.get<{ data: ProtocolTemplate }>(`/protocols/templates/${templateId}`),

  /** Fork a template into a new org-owned template (admin only) */
  forkTemplate: (templateId: number) =>
    api.post<{ data: ProtocolTemplate }>(`/protocols/templates/${templateId}/fork`),

  /** Update an org-owned template (admin / creator only) */
  updateTemplate: (
    templateId: number,
    data: {
      title?: string;
      description?: string | null;
      content?: string;
      category_id?: number | null;
      importance?: string;
      tags?: string[];
    }
  ) =>
    api.put<{ data: ProtocolTemplate }>(`/protocols/templates/${templateId}`, data),

  /** Soft-delete an org-owned template (admin / creator only) */
  deleteTemplate: (templateId: number) =>
    api.delete<{ success: boolean }>(`/protocols/templates/${templateId}`),

  // =============================================================================
  // Compliance (Spec 101 + 110)
  // =============================================================================

  /** Get compliance data for all active protocols */
  getCompliance: (options?: { periodDays?: number }) => {
    const params = new URLSearchParams();
    if (options?.periodDays) params.append('periodDays', options.periodDays.toString());
    const qs = params.toString();
    return api.get<{ data: ProtocolComplianceData[] }>(`/protocols/compliance${qs ? `?${qs}` : ''}`);
  },

  /** Get compliance summary for the hierarchy */
  getComplianceSummary: () =>
    api.get<{ data: ComplianceSummary }>('/protocols/compliance/summary'),

  /** Get compliance data for a specific user */
  getUserCompliance: (userId: number | string, options?: { periodDays?: number }) => {
    const params = new URLSearchParams();
    if (options?.periodDays) params.append('periodDays', options.periodDays.toString());
    const qs = params.toString();
    return api.get<{ data: ProtocolComplianceData[] }>(
      `/protocols/compliance/user/${userId}${qs ? `?${qs}` : ''}`
    );
  },

  /** Get compliance heatmap for a specific assignment */
  getComplianceHeatmap: (assignmentId: number, options?: { periodDays?: number }) => {
    const params = new URLSearchParams();
    if (options?.periodDays) params.append('periodDays', options.periodDays.toString());
    const qs = params.toString();
    return api.get<{ data: HeatmapEntry[] }>(
      `/protocols/compliance/heatmap/${assignmentId}${qs ? `?${qs}` : ''}`
    );
  },

  /** Record a compliance event */
  recordComplianceEvent: (data: {
    assignmentId: number;
    eventType: 'completed' | 'missed' | 'excused';
    eventDate: string;
    notes?: string;
  }) => api.post<{ data: unknown }>('/protocols/compliance/event', data),

  /** Report a protocol lapse */
  reportLapse: (data: {
    protocolId: number | string;
    assignmentId: number | string;
    lapseDate: string;
    description?: string;
  }) => api.post<{ data: LapseReport }>('/protocols/compliance/lapse', data),

  /** Acknowledge a lapse report (admin only) */
  acknowledgeLapse: (lapseId: number) =>
    api.post<{ data: LapseReport }>(`/protocols/compliance/lapse/${lapseId}/acknowledge`),

  /** Get lapse reports */
  getLapseReports: (options?: { protocolId?: number; acknowledged?: boolean; limit?: number }) => {
    const params = new URLSearchParams();
    if (options?.protocolId) params.append('protocolId', options.protocolId.toString());
    if (options?.acknowledged !== undefined) params.append('acknowledged', options.acknowledged.toString());
    if (options?.limit) params.append('limit', options.limit.toString());
    const qs = params.toString();
    return api.get<{ data: LapseReport[] }>(`/protocols/compliance/lapses${qs ? `?${qs}` : ''}`);
  },
};
